import { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Badge from '../components/Badge.jsx';
import StrategicAccountForm from '../components/StrategicAccountForm.jsx';
import ContactsSection from '../components/ContactsSection.jsx';
import SendEmailDialog from '../components/SendEmailDialog.jsx';
import CallsTab from '../components/CallsTab.jsx';
import EmailsTab from '../components/EmailsTab.jsx';
import { ACCOUNT_KINDS } from '../lib/accountKinds.js';

// Strategic Account detail page. Layout mirrors DistributorDetail:
// record header with Send Email + Delete actions, tab strip (Details /
// Notes / Documents), then the flat contact form + Strategic Account
// Details section + Contacts (sub-contacts) section.

const TABS = [
  { id: 'details',   label: 'Details' },
  { id: 'notes',     label: 'Notes' },
  { id: 'calls',     label: 'Calls' },
  { id: 'emails', label: 'Emails' },
  { id: 'documents', label: 'Documents' }
];

function displayName(a) {
  if (a?.company) return a.company;
  const n = [a?.first_name, a?.last_name].filter(Boolean).join(' ');
  return n || `Strategic Account #${a?.id ?? ''}`;
}

export default function StrategicAccountDetail({ kind = 'strategic' }) {
  const K = ACCOUNT_KINDS[kind] || ACCOUNT_KINDS.strategic;
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState('details');
  const [saveMessage, setSaveMessage] = useState(null);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [emailSubContact, setEmailSubContact] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${K.api}/${id}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id, K.api]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdate(form) {
    await axios.put(`${K.api}/${id}`, form);
    setSaveMessage('Saved.');
    await load();
    setTimeout(() => setSaveMessage(null), 2500);
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.delete(`${K.api}/${id}`);
      navigate(K.path);
    } catch (err) {
      setDeleteError(err.response?.data?.error || err.message);
      setDeleting(false);
    }
  }

  if (loading && !data) {
    return <section className="page"><p>Loading…</p></section>;
  }
  if (error && !data) {
    return (
      <section className="page">
        <div className="page-header">
          <h2>{K.single}</h2>
          <Link to={K.path} className="btn-secondary">← Back to {K.title}</Link>
        </div>
        <p className="error">{error}</p>
      </section>
    );
  }

  const acct = data.strategic_account;
  const location = [acct.city, acct.province_state].filter(Boolean).join(', ');
  const contactName = [acct.first_name, acct.last_name].filter(Boolean).join(' ');

  return (
    <section className="page">
      <div className="page-header">
        <Link to={K.path} className="btn-secondary">← Back to {K.title}</Link>
      </div>

      <div className="record-header">
        <div className="record-title" style={{ flex: 1 }}>
          <h2>{displayName(acct)}</h2>
          <div className="record-meta">
            {acct.position && <>{acct.position}</>}
            {acct.territory && <><span className="sep" /><Badge tone="purple">{acct.territory}</Badge></>}
            {location && <><span className="sep" />{location}</>}
            {contactName && acct.company && <><span className="sep" />{contactName}</>}
          </div>
        </div>
        <div className="record-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSendEmailOpen(true)}
            disabled={deleting}
            title="Send a templated email to this contact"
          >
            Send Email
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={deleting}
            title="Delete this strategic account and all related records"
          >
            Delete
          </button>
        </div>
      </div>

      <SendEmailDialog
        open={sendEmailOpen}
        record={acct}
        recordType={K.recordType}
        onClose={() => setSendEmailOpen(false)}
        onSent={() => { load(); }}
      />

      {/* Sub-contact email dialog -- synthesizes a record that looks like
          the strategic account but with the sub-contact's name + email,
          so template merge tokens render the sub-contact and the To
          field defaults to their address. Mirrors DistributorDetail. */}
      {emailSubContact && (
        <SendEmailDialog
          open={!!emailSubContact}
          record={{
            ...acct,
            first_name: emailSubContact.first_name || emailSubContact.name || '',
            last_name:  emailSubContact.last_name  || '',
            position:   emailSubContact.position   || acct.position,
            phone_main: emailSubContact.phone1     || acct.phone_main,
            email1:     emailSubContact.email      || '',
            email2:     null,
            email3:     null,
            sub_contact_id: emailSubContact.id
          }}
          recordType={K.recordType}
          onClose={() => setEmailSubContact(null)}
          onSent={() => { load(); setEmailSubContact(null); }}
        />
      )}

      {/* Destructive-delete confirmation -- matches the distributor modal. */}
      {deleteConfirmOpen && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !deleting) setDeleteConfirmOpen(false);
          }}
        >
          <div className="modal-card modal-narrow" role="dialog" aria-modal="true" aria-label="Delete strategic account">
            <header className="modal-head">
              <h3>Delete {K.single}</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
                aria-label="Close"
              >×</button>
            </header>
            <div className="modal-body">
              <p>Are you sure you want to delete this {K.single.toLowerCase()}? This cannot be undone.</p>
              <p className="modal-sub">
                Deleting <strong>{displayName(acct)}</strong> will remove the
                contact record plus all related notes, contacts, and
                documents (cascade).
              </p>
              {deleteError && <p className="error">{deleteError}</p>}
            </div>
            <footer className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : `Delete ${K.single.toLowerCase()}`}
              </button>
            </footer>
          </div>
        </div>
      )}

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab${active === t.id ? ' tab-active' : ''}`}
            onClick={() => setActive(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="tab-panel">
        {saveMessage && <p className="save-message">{saveMessage}</p>}

        {active === 'details' && (
          <>
            <StrategicAccountForm
              key={acct.id}
              initial={acct}
              onSubmit={handleUpdate}
              submitLabel="Save Changes"
            />
            <ContactsSection
              contactId={acct.id}
              items={acct.sub_contacts || []}
              onChanged={load}
              onSendEmail={(subContact) => setEmailSubContact(subContact)}
            />
          </>
        )}

        {active === 'notes' && (
          <p className="panel-empty">Notes tab — coming soon.</p>
        )}

        {active === 'calls' && <CallsTab contactId={acct.id} />}
        {active === 'emails' && <EmailsTab contactId={acct.id} />}

        {active === 'documents' && (
          <p className="panel-empty">Documents tab — coming soon.</p>
        )}
      </div>
    </section>
  );
}
